import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import OrderService from '../features/order/OrderService';

// Section 5 — ephemeral confirmation modal. Cannot be closed (no X, no
// click-away, no escape). Admin must pick one matched orderer and save.
// If they refresh the page the modal is gone — by design.
const OrdererConfirmModal = ({ ordererMatch, orderByOptions = [], onConfirmed }) => {
  const [choice, setChoice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pre-select the orderer the admin already assigned, so a wrong LINE match
  // (e.g. link sent to the wrong group) can be dismissed by just saving.
  useEffect(() => {
    setChoice(
      ordererMatch?.assignedOrderByOptionId != null
        ? String(ordererMatch.assignedOrderByOptionId)
        : ''
    );
  }, [ordererMatch]);

  if (!ordererMatch) return null;

  const { orderId, url, smileNameInput, contact, matchedOrderers = [], assignedOrderByOptionId, hasMissingRequiredLink } = ordererMatch;

  // Show every orderer (backend accepts any), tagging the LINE-matched one(s).
  const matchedById = new Map(matchedOrderers.map((o) => [o.id, o]));
  const options = orderByOptions.length ? orderByOptions : matchedOrderers;

  const handleConfirm = async () => {
    if (choice === '') return;
    setSubmitting(true);
    try {
      await OrderService.confirmOrderer({
        orderId,
        confirmedOrderByOptionId: Number(choice),
      });
      toast.success('ยืนยัน orderer เรียบร้อย');
      if (onConfirmed) onConfirmed();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ยืนยันไม่สำเร็จ');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={true}
      disableEscapeKeyDown
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>ยืนยัน Orderer</DialogTitle>
      <DialogContent>
        {hasMissingRequiredLink ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            ไม่พบลิงก์ที่โพสต์ใน LINE group สำหรับ orderer นี้ (orderer ตั้งค่าให้ต้องมี LINE link ก่อน push)
            — กรุณายืนยัน orderer ที่ถูกต้อง หรือรอให้ลิงก์ถูกโพสต์ใน LINE group ก่อน
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            กรุณายืนยัน ผู้สั่ง (orderBy)
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          {smileNameInput && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Smile Name:</strong> {smileNameInput}
            </Typography>
          )}
          {contact && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Contact:</strong> {contact}
            </Typography>
          )}
          {url && (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              <strong>URL:</strong>{' '}
              <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
            </Typography>
          )}
        </Box>
        <FormControl fullWidth>
          <InputLabel id="orderer-confirm-label">เลือก Orderer</InputLabel>
          <Select
            labelId="orderer-confirm-label"
            value={choice}
            label="เลือก Orderer"
            onChange={(e) => setChoice(e.target.value)}
          >
            {options.map((o) => {
              const matched = matchedById.get(o.id);
              const isAssigned =
                assignedOrderByOptionId != null && o.id === Number(assignedOrderByOptionId);
              return (
                <MenuItem key={o.id} value={String(o.id)}>
                  {o.label}{o.value ? ` (${o.value})` : ''}
                  {matched && (
                    <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                      ✓ จาก LINE: {matched.lineGroupName || matched.lineGroupId}
                    </Typography>
                  )}
                  {isAssigned && (
                    <Typography component="span" variant="caption" color="primary" sx={{ ml: 1 }}>
                      ← ที่ระบุไว้
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={submitting || choice === ''}
        >
          {submitting ? 'กำลังบันทึก...' : 'บันทึก'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrdererConfirmModal;
